import { Pouch, Group } from '../types';
import { isInMuteGroup } from './GroupLogic';

/** 時間パウチの内部値の絶対値（UI の「±60×」と対応。負の時間は value=-60・マルチは正の分率） */
export const TIME_POUCH_BALANCING_BASE_VALUE = 60;

/** 時間パウチの合計への寄与（常に value×multiplier） */
export const getTimePouchEffectiveForTotal = (p: Pouch): number => {
  return p.value * p.multiplier;
};

/**
 * 表示・サイズ計算用: 時間の実効分数（レガシー除算モードは分岐）
 * multiplierOverride: 一時マルチ（ボタン操作中）
 */
export const getTimePouchEffectiveMinutes = (
  p: Pick<Pouch, 'value' | 'multiplier' | 'isTime'>,
  multiplierOverride?: number
): number => {
  const m = multiplierOverride !== undefined ? multiplierOverride : p.multiplier;
  if (!p.isTime) return p.value;
  return m >= 0 ? p.value * m : p.value / Math.abs(m);
};

/** シート・グループ小計と同じ定義のパウチ1個分の実効値 */
export const getPouchEffectiveContribution = (p: Pouch): number => {
  if (p.isTime === true) {
    return getTimePouchEffectiveForTotal(p);
  }
  return p.multiplier < 0
    ? p.value / Math.abs(p.multiplier)
    : p.value * p.multiplier;
};

/**
 * 帳尻用: 必要な実効値から value=±60 と multiplier（分率・非負）を求める
 */
export const calculateBalancingPouchTimeState = (
  pouches: Pouch[],
  groups: Group[],
  balancingPouchId: string,
  targetTotal: number
): { value: number; multiplier: number } => {
  const bpPouch = pouches.find(p => p.id === balancingPouchId);
  if (!bpPouch) return { value: 0, multiplier: 0 };

  const otherTotal = calculateNonBalancingTotal(pouches, groups, balancingPouchId);
  const requiredEffective = targetTotal - otherTotal;
  const base = TIME_POUCH_BALANCING_BASE_VALUE;

  if (requiredEffective === 0) {
    return { value: base, multiplier: 0 };
  }

  const value = requiredEffective > 0 ? base : -base;
  const multiplier = requiredEffective / value;
  return {
    value,
    multiplier: Math.round(multiplier * 1000000000) / 1000000000
  };
};

/**
 * BP 以外の全パウチの effectiveValue 合計を計算する
 */
export const calculateNonBalancingTotal = (
  pouches: Pouch[],
  groups: Group[],
  balancingPouchId: string
): number => {
  return pouches
    .filter(p =>
      p.id !== balancingPouchId &&
      !p._isDeleting &&
      !isInMuteGroup(p.id, groups)
    )
    .reduce((sum, p) => {
      const effective = p.isTime === true
        ? getTimePouchEffectiveForTotal(p)
        : p.multiplier < 0
          ? p.value / Math.abs(p.multiplier)
          : p.value * p.multiplier;
      return sum + effective;
    }, 0);
};

/**
 * 全パウチの合計を計算する（ターゲット記録用）
 */
export const calculateSheetTotal = (
  pouches: Pouch[],
  groups: Group[]
): number => {
  const total = pouches
    .filter(p => !p._isDeleting && !isInMuteGroup(p.id, groups))
    .reduce((sum, p) => {
      const effective = p.isTime === true
        ? getTimePouchEffectiveForTotal(p)
        : p.multiplier < 0
          ? p.value / Math.abs(p.multiplier)
          : p.value * p.multiplier;
      return sum + effective;
    }, 0);
  return Math.round(total * 1000000000) / 1000000000;
};

/**
 * BP パウチの新しい value を計算する
 * ターゲット合計に最も近くなる整数値を返す
 */
export const calculateBalancingPouchValue = (
  pouches: Pouch[],
  groups: Group[],
  balancingPouchId: string,
  targetTotal: number
): number => {
  const bpPouch = pouches.find(p => p.id === balancingPouchId);
  if (!bpPouch) return 0;

  const otherTotal = calculateNonBalancingTotal(pouches, groups, balancingPouchId);
  const requiredEffective = targetTotal - otherTotal;
  const multiplier = bpPouch.multiplier;

  if (multiplier === 0) return 0;

  let rawValue: number;
  if (multiplier < 0) {
    rawValue = requiredEffective * Math.abs(multiplier);
  } else {
    rawValue = requiredEffective / multiplier;
  }

  return Math.round(rawValue);
};

/**
 * BP 再計算を適用した pouches 配列を返す
 * 通常 BP は value のみ更新。時間パウチ BP は value=±60 と multiplier を更新する。
 */
export const applyBalancingPouch = (
  pouches: Pouch[],
  groups: Group[],
  balancingPouchId: string,
  targetTotal: number
): Pouch[] => {
  const bpPouch = pouches.find(p => p.id === balancingPouchId);
  if (!bpPouch) return pouches;

  if (bpPouch.isTime === true) {
    const { value: newValue, multiplier: newMultiplier } = calculateBalancingPouchTimeState(
      pouches,
      groups,
      balancingPouchId,
      targetTotal
    );
    return pouches.map(p =>
      p.id === balancingPouchId
        ? { ...p, value: newValue, multiplier: newMultiplier }
        : p
    );
  }

  const newValue = calculateBalancingPouchValue(pouches, groups, balancingPouchId, targetTotal);
  return pouches.map(p =>
    p.id === balancingPouchId ? { ...p, value: newValue } : p
  );
};
